// src/utils/icsUtils.js
// Generador de invitaciones de calendario (.ics) sin dependencias.
//
// Uso principal: enviar a los miembros asignados a un evento una invitación
// que su Gmail/Google Calendar reconozca y permita añadir a su calendario.
//
// Limitación conocida: los eventos no tienen hora estructurada en la BD, así que
// generamos un VEVENT de DÍA COMPLETO (DTSTART;VALUE=DATE). Cuando exista un campo
// de hora de convocatoria se podrá pasar a DTSTART con hora (ver TODO abajo).

const DOMAIN = "wearelostoldos.com";

/**
 * Escapa un texto para un valor de propiedad iCalendar (RFC 5545).
 * Escapa \\, ; , y saltos de línea.
 */
function escapeIcsText(value) {
  return String(value ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/**
 * Convierte 'YYYY-MM-DD' a 'YYYYMMDD' (formato DATE de iCalendar).
 */
function toIcsDate(isoDate) {
  return String(isoDate).slice(0, 10).replace(/-/g, "");
}

/**
 * Suma un día a 'YYYY-MM-DD' y devuelve 'YYYYMMDD'.
 * En VEVENT de día completo, DTEND es exclusivo (día siguiente).
 */
function nextDayIcsDate(isoDate) {
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

/**
 * Timestamp UTC actual en formato iCalendar (DTSTAMP): 'YYYYMMDDTHHMMSSZ'.
 */
function nowIcsStamp() {
  return new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

/**
 * Envuelve líneas a 75 octetos según RFC 5545 (folding con espacio inicial).
 */
function foldLine(line) {
  if (line.length <= 75) return line;
  const parts = [];
  let idx = 0;
  parts.push(line.slice(0, 75));
  idx = 75;
  while (idx < line.length) {
    parts.push(" " + line.slice(idx, idx + 74));
    idx += 74;
  }
  return parts.join("\r\n");
}

/**
 * Construye un VCALENDAR/VEVENT completo (día completo) para un evento.
 *
 * @param {Object} params
 * @param {string} params.eventId       - UUID del evento (para UID estable).
 * @param {number} [params.sequence=0]  - Nº de secuencia; súbelo en re-envíos para actualizar.
 * @param {string} params.summary       - Título del evento (ej. client_name_legacy).
 * @param {string} [params.description] - Descripción libre.
 * @param {string} [params.location]    - Lugar/ciudad.
 * @param {string} params.date          - Fecha 'YYYY-MM-DD'.
 * @param {string} params.organizerEmail- Email del emisor (ORGANIZER).
 * @param {string} [params.organizerName]
 * @param {Array<{email:string,name?:string}>} params.attendees - Destinatarios (ATTENDEE).
 * @returns {string} Contenido .ics con saltos de línea CRLF.
 */
export function buildEventIcs({
  eventId,
  sequence = 0,
  summary,
  description = "",
  location = "",
  date,
  organizerEmail,
  organizerName = "Los Toldos",
  attendees = [],
}) {
  const uid = `evt-${eventId}@${DOMAIN}`;
  const dtStart = toIcsDate(date);
  const dtEnd = nextDayIcsDate(date);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:-//Los Toldos CRM//Invite//ES`,
    "CALSCALE:GREGORIAN",
    "METHOD:REQUEST",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `SEQUENCE:${sequence}`,
    `DTSTAMP:${nowIcsStamp()}`,
    `DTSTART;VALUE=DATE:${dtStart}`,
    `DTEND;VALUE=DATE:${dtEnd}`,
    `SUMMARY:${escapeIcsText(summary)}`,
    ...(description ? [`DESCRIPTION:${escapeIcsText(description)}`] : []),
    ...(location ? [`LOCATION:${escapeIcsText(location)}`] : []),
    `ORGANIZER;CN=${escapeIcsText(organizerName)}:mailto:${organizerEmail}`,
    ...attendees
      .filter((a) => a && a.email)
      .map(
        (a) =>
          `ATTENDEE;CN=${escapeIcsText(a.name || a.email)};ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE:mailto:${a.email}`
      ),
    "STATUS:CONFIRMED",
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  return lines.map(foldLine).join("\r\n");
}

/**
 * Codifica una cadena .ics a base64 (para adjuntarla vía la Edge Function/Resend).
 * Maneja correctamente UTF-8 (acentos, ñ) en el navegador.
 */
export function icsToBase64(icsString) {
  return btoa(unescape(encodeURIComponent(icsString)));
}

// TODO(futuro): cuando events tenga un campo de hora de convocatoria, cambiar el
// VEVENT a DTSTART con hora (TZID=Europe/Madrid) en vez de VALUE=DATE.
